import { useCallback, useRef } from 'react';
import { useGenerationStore } from '../stores/generation-store';
import { useHistoryStore } from '../stores/history-store';
import { useEditorStore } from '../stores/editor-store';
import { useSettingsStore } from '../stores/settings-store';
import { useProjectsStore } from '../stores/projects-store';
import { useAiActivityStore } from '../stores/ai-activity-store';
import { useSectionsBuilderStore } from '../stores/sections-builder-store';
import { useLanguageStore } from '../stores/language-store';
import { constructPromptWithSettings, buildSectionsPrompt } from '../utils/prompt-utils';
import { getPageSnapshots } from '../utils/grapes-page-snapshots';
import { getHtmlWarnings, normalizeGeneratedHtml } from '../utils/html-qc';
import { improveHtmlLocally } from '../utils/html-improve-local';
import {
  generateHtmlWithUserKey,
  getCallableErrorCode,
  getCallableErrorMessage,
} from '../services/ai-functions';
import { useAiAccessStore } from '../stores/ai-access-store';

function clampText(input: string, maxLen: number) {
  if (input.length <= maxLen) return input;
  return `${input.slice(0, maxLen)}\n\n[TRUNCATED ${input.length - maxLen} CHARS]`;
}

async function buildReferenceBlock(params: {
  currentProject: any;
  editor: any;
  reference: { versionId: string; pageId: string } | null;
  loadProjectContent: (projectId: string, opts?: { versionId?: string }) => Promise<any>;
}): Promise<string> {
  const { currentProject, editor, reference, loadProjectContent } = params;
  if (!currentProject || !reference) return '';

  const meta = currentProject.contentVersionMeta ?? [];
  const versionIdx = meta.findIndex((m: any) => m?.id === reference.versionId);
  const versionLabel = versionIdx >= 0 ? String(versionIdx + 1) : 'unknown';

  // Prefer current editor snapshots if referencing the active version.
  const activeId = currentProject.activeContentVersionId ?? meta[meta.length - 1]?.id;
  let pageSnapshots: Array<{ id: string; name: string; html: string; css: string }> | undefined;

  if (editor && reference.versionId === activeId) {
    pageSnapshots = getPageSnapshots(editor);
  } else {
    const content = await loadProjectContent(currentProject.id, { versionId: reference.versionId });
    pageSnapshots = content?.pageSnapshots;
  }

  const page = pageSnapshots?.find((p) => String(p.id) === String(reference.pageId));
  if (!page) return '';

  const html = clampText(String(page.html ?? ''), 8000);
  const css = clampText(String(page.css ?? ''), 8000);
  const name = String(page.name ?? 'Page');

  return [
    '',
    '[REFERENCE]',
    `- Version: ${versionLabel}`,
    `- Page: ${name}`,
    '- Use this as a reference (structure, components, style). Do not copy 1:1; adapt to the user request.',
    '',
    '[REFERENCE_HTML]',
    html,
    '[/REFERENCE_HTML]',
    '',
    '[REFERENCE_CSS]',
    css,
    '[/REFERENCE_CSS]',
    '[/REFERENCE]',
    '',
  ].join('\n');
}

export function useGenerate() {
  const prompt = useGenerationStore((s) => s.prompt);
  const reference = useGenerationStore((s) => s.reference);
  const isGenerating = useGenerationStore((s) => s.isGenerating);
  const setWarnings = useGenerationStore((s) => s.setWarnings);
  const { startGeneration, generationSuccess, generationError } = useGenerationStore();
  const currentProject = useProjectsStore((s) => s.currentProject);
  const loadProjectContent = useProjectsStore((s) => s.loadProjectContent);
  const addEntry = useHistoryStore((s) => s.addEntry);
  const loadContent = useEditorStore((s) => s.loadContent);
  const editor = useEditorStore((s) => s.editorInstance);
  const saveCurrentContent = useProjectsStore((s) => s.saveCurrentContent);
  const setSettingsOpen = useSettingsStore((s) => s.setSettingsOpen);
  const startAiActivity = useAiActivityStore((s) => s.start);
  const stopAiActivity = useAiActivityStore((s) => s.stop);
  const setUsage = useAiAccessStore((s) => s.setUsage);
  const refreshStatus = useAiAccessStore((s) => s.refreshStatus);
  const selectedSections = useSectionsBuilderStore((s) => s.selectedSections);
  const density = useSectionsBuilderStore((s) => s.density);
  const language = useLanguageStore((s) => s.language);
  const generatingRef = useRef(false);

  const generate = useCallback(async () => {
    if (!prompt.trim() || generatingRef.current) return;

    generatingRef.current = true;
    startAiActivity();
    const controller = new AbortController();
    controller.signal.addEventListener(
      'abort',
      () => {
        generatingRef.current = false;
      },
      { once: true }
    );
    startGeneration(controller, 'generate');

    try {
      const userPrompt = constructPromptWithSettings(prompt, currentProject?.generationSettings);
      const sectionsPrompt = buildSectionsPrompt(selectedSections, density, language);
      const referenceBlock = await buildReferenceBlock({
        currentProject,
        editor,
        reference,
        loadProjectContent: async (projectId, opts) => loadProjectContent(projectId, opts),
      });
      const combinedPrompt = `${userPrompt}${sectionsPrompt}${referenceBlock}`;
      const fullPrompt = `Generate a complete HTML page with inline Tailwind CSS classes based on this description:

${combinedPrompt}

Requirements:
- Use Tailwind CSS utility classes (the page will have Tailwind CDN loaded)
- Create a responsive, modern design
- Include proper semantic HTML structure
- Make it visually appealing with good spacing, colors, and typography
- Ensure proper spacing between all elements, no overlapping
- Do NOT include <script>, <link>, <style> tags, or inline event handlers (onClick/onLoad/etc)
- Every icon-only button/link must have aria-label (avoid QC warnings)
- Return ONLY the HTML code without explanations or markdown code blocks
- The HTML should be a complete body content (no <!DOCTYPE>, <html>, or <head> tags)

Generate the HTML now:`;

      const result = await generateHtmlWithUserKey(fullPrompt);

      if (controller.signal.aborted) {
        generationError('Generation cancelled');
        return;
      }

      setUsage(result.usage);
      const normalizedHtml = normalizeGeneratedHtml(result.html);
      setWarnings(getHtmlWarnings(normalizedHtml).map((w) => w.message));
      loadContent(normalizedHtml);
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (editor) {
        const html = editor.getHtml() || '';
        const css = editor.getCss() || '';
        const components = editor.getComponents().toJSON();
        const styles = editor.getStyle();
        const projectData = (editor as any).getProjectData ? (editor as any).getProjectData() : undefined;
        const pageSnapshots = getPageSnapshots(editor as any);
        await saveCurrentContent({ html, css, components, styles, projectData, pageSnapshots });
      }
      addEntry({ prompt, html: normalizedHtml, css: '' });
      generationSuccess();
    } catch (err) {
      if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError')) {
        generationError('Generation cancelled');
      } else {
        const code = getCallableErrorCode(err);
        const message = getCallableErrorMessage(err);

        if (code === 'functions/failed-precondition' || message.toLowerCase().includes('key')) {
          setSettingsOpen(true);
          void refreshStatus();
        }

        generationError(message);
      }
    } finally {
      generatingRef.current = false;
      stopAiActivity();
    }
  }, [
    prompt,
    startGeneration,
    generationSuccess,
    generationError,
    currentProject?.generationSettings,
    reference,
    loadProjectContent,
    setUsage,
    setWarnings,
    loadContent,
    editor,
    saveCurrentContent,
    addEntry,
    setSettingsOpen,
    refreshStatus,
    startAiActivity,
    stopAiActivity,
    selectedSections,
    density,
    language,
  ]);

  const improve = useCallback(async () => {
    const editor = useEditorStore.getState().editorInstance as any;
    if (!editor || generatingRef.current) return;

    generatingRef.current = true;
    const controller = new AbortController();
    controller.signal.addEventListener(
      'abort',
      () => {
        generatingRef.current = false;
      },
      { once: true }
    );
    startGeneration(controller, 'improve');

    try {
      const currentHtml = String(editor.getHtml?.() ?? '');
      const normalizedCurrentHtml = normalizeGeneratedHtml(currentHtml);
      const initialWarnings = getHtmlWarnings(normalizedCurrentHtml);
      setWarnings(initialWarnings.map((w) => w.message));

      const improvedHtml = improveHtmlLocally({
        html: normalizedCurrentHtml,
        backgroundColor: currentProject?.generationSettings?.backgroundColor,
      });
      setWarnings(getHtmlWarnings(improvedHtml).map((w) => w.message));
      loadContent(improvedHtml);

      await new Promise((resolve) => setTimeout(resolve, 0));
      if (currentProject) {
        const html = editor.getHtml?.() || '';
        const css = editor.getCss?.() || '';
        const components = editor.getComponents?.().toJSON?.() ?? [];
        const styles = editor.getStyle?.() ?? [];
        const projectData = editor.getProjectData ? editor.getProjectData() : undefined;
        const pageSnapshots = getPageSnapshots(editor);
        await saveCurrentContent(
          { html, css, components, styles, projectData, pageSnapshots },
          { mode: 'new', comment: 'Improve (local)' }
        );
      }

      addEntry({ prompt: prompt ? `[Improve local] ${prompt}` : '[Improve local]', html: improvedHtml, css: '' });
      generationSuccess();
    } catch (err) {
      if (controller.signal.aborted || (err instanceof Error && err.name === 'AbortError')) {
        generationError('Generation cancelled');
      } else {
        generationError(err instanceof Error ? err.message : 'Failed to improve');
      }
    } finally {
      generatingRef.current = false;
    }
  }, [
    prompt,
    startGeneration,
    generationSuccess,
    generationError,
    currentProject?.generationSettings,
    currentProject?.id,
    setWarnings,
    loadContent,
    saveCurrentContent,
    addEntry,
    selectedSections,
    density,
    language,
  ]);

  return { generate, improve, prompt, isGenerating };
}
